import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IAuthorities, defaultValue } from 'app/shared/model/authorities.model';

const initialState: EntityState<IAuthorities> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/authorities/v1';

// Actions

export const getEntities = createAsyncThunk('authorities/fetch_entity_list', async ({ filterUrl, page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}?${filterUrl ? filterUrl + '&' : ''}${
    sort ? `page=${page}&size=${size}&sort=${sort}&` : ''
  }cacheBuster=${new Date().getTime()}`;
  return axios.get<IAuthorities[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'authorities/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IAuthorities>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getAuthorities = createAsyncThunk(
  'authorities/fetch_entity_internal',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}?internalUserId.equals=${id}`;
    return axios.get<IAuthorities[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'authorities/create_entity',
  async (entity: IAuthorities, thunkAPI) => {
    const result = await axios.post<IAuthorities>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'authorities/update_entity',
  async (entity: IAuthorities, thunkAPI) => {
    const result = await axios.put<IAuthorities>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'authorities/partial_update_entity',
  async (entity: IAuthorities, thunkAPI) => {
    const result = await axios.patch<IAuthorities>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'authorities/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IAuthorities>(requestUrl);
    thunkAPI.dispatch(getEntities({ page: 0, size: 5, sort: 'name,asc' }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const authoritiesSlice = createEntitySlice({
  name: 'authorities',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = authoritiesSlice.actions;

// Reducer
export default authoritiesSlice.reducer;
